// Receipt Printer Service
// Supports browser print dialog and ESC/POS thermal printers.
// On iOS, 'tcp' sends raw ESC/POS to LAN printers (port 9100) through the
// native Printer plugin — WKWebView has no USB or TCP access of its own.

import { Capacitor, registerPlugin } from '@capacitor/core';

const NativePrinter = registerPlugin<{
  sendRaw(options: { ip: string; port?: number; data: string }): Promise<void>;
}>('Printer');

interface PrinterConfig {
  type: 'browser' | 'thermal' | 'network' | 'tcp';
  paperWidth: '58mm' | '80mm';
  networkUrl?: string;
  /** LAN IP of the receipt printer (guest receipts) */
  receiptIp?: string;
  /** LAN IP of the kitchen printer (tickets); falls back to receiptIp */
  kitchenIp?: string;
}

const CONFIG_STORAGE_KEY = 'mise-pos-printer-config';

interface ReceiptData {
  restaurantName: string;
  restaurantAddress?: string;
  restaurantPhone?: string;
  checkNumber: string;
  tableNumber?: string;
  serverName?: string;
  guestName?: string;
  items: Array<{
    name: string;
    quantity: number;
    priceCents: number;
    modifiers?: Array<{ name: string; priceCents: number }>;
  }>;
  subtotalCents: number;
  taxCents: number;
  tipCents?: number;
  discountCents?: number;
  discountReason?: string;
  totalCents: number;
  paymentMethod?: string;
  amountTenderedCents?: number;
  changeCents?: number;
  printedAt: Date;
}

class PrinterService {
  private config: PrinterConfig = {
    type: 'browser',
    paperWidth: '80mm',
  };

  constructor() {
    // Restore the saved printer setup (per device)
    try {
      const stored = localStorage.getItem(CONFIG_STORAGE_KEY);
      if (stored) this.config = { ...this.config, ...JSON.parse(stored) };
    } catch {
      // corrupted config — defaults apply
    }
  }

  public configure(config: Partial<PrinterConfig>) {
    this.config = { ...this.config, ...config };
    try {
      localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(this.config));
    } catch {
      // storage unavailable — config lives for the session only
    }
  }

  public getConfig(): PrinterConfig {
    return { ...this.config };
  }

  /**
   * Adopt the restaurant-wide printer setup (managers configure it once in
   * the dashboard; every POS device inherits it). Called at app start.
   */
  public syncFromRestaurant(printers?: { receiptIp?: string | null; kitchenIp?: string | null }) {
    if (!printers) return;
    const receiptIp = printers.receiptIp || undefined;
    const kitchenIp = printers.kitchenIp || undefined;
    if (receiptIp) {
      this.configure({ type: 'tcp', receiptIp, kitchenIp });
    } else if (this.config.type === 'tcp' && !receiptIp) {
      // Printers were removed restaurant-wide — fall back gracefully.
      this.configure({ type: 'browser', receiptIp: undefined, kitchenIp: undefined });
    }
  }

  /** Raw ESC/POS over the native TCP bridge (iOS LAN printers, port 9100). */
  private async sendTcp(bytes: Uint8Array, ip: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('TCP printing is only available in the iOS app.');
    }
    // btoa in chunks — spreading a large Uint8Array overflows the call stack
    let binary = '';
    const CHUNK = 8192;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    await NativePrinter.sendRaw({ ip, port: 9100, data: btoa(binary) });
  }

  /** A tiny ticket to confirm a printer is reachable and cutting. */
  public async testPrint(ip: string): Promise<void> {
    const encoder = new EscPosEncoder();
    encoder.init();
    encoder.align('center');
    encoder.bold(true);
    encoder.size(2);
    encoder.text('Mise');
    encoder.newline();
    encoder.size(1);
    encoder.bold(false);
    encoder.text('Printer connected');
    encoder.newline();
    encoder.text(new Date().toLocaleString());
    encoder.newline();
    encoder.cut();
    await this.sendTcp(encoder.getBuffer(), ip);
  }

  public async printReceipt(data: ReceiptData): Promise<void> {
    // Validate data
    if (!data.items || data.items.length === 0) {
      console.warn('PrinterService: No items to print');
    }
    console.log('PrinterService: Printing receipt', {
      restaurant: data.restaurantName,
      items: data.items?.length || 0,
      total: data.totalCents
    });

    switch (this.config.type) {
      case 'browser':
        return this.printBrowser(data);
      case 'thermal':
        return this.printThermal(data);
      case 'network':
        return this.printNetwork(data);
      case 'tcp': {
        if (!this.config.receiptIp) throw new Error('Receipt printer IP not set — open Printers in the header.');
        return this.sendTcp(this.buildEscPosCommands(data), this.config.receiptIp);
      }
      default:
        return this.printBrowser(data);
    }
  }

  private formatCurrency(cents: number): string {
    return `$${(cents / 100).toFixed(2)}`;
  }

  private formatDate(date: Date): string {
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Browser print using a popup window
  private async printBrowser(data: ReceiptData): Promise<void> {
    const width = this.config.paperWidth === '58mm' ? 220 : 300;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              width: ${width}px;
              padding: 10px;
              background: white;
            }
            .center { text-align: center; }
            .right { text-align: right; }
            .bold { font-weight: bold; }
            .line { border-top: 1px dashed #000; margin: 8px 0; }
            .double-line { border-top: 2px solid #000; margin: 8px 0; }
            .header { margin-bottom: 15px; }
            .header h1 { font-size: 18px; margin-bottom: 5px; }
            .header p { font-size: 10px; }
            .meta { margin-bottom: 10px; }
            .meta-row { display: flex; justify-content: space-between; font-size: 11px; }
            .items { margin-bottom: 10px; }
            .item { margin-bottom: 8px; }
            .item-row { display: flex; justify-content: space-between; }
            .item-name { flex: 1; }
            .modifier { padding-left: 15px; font-size: 10px; color: #444; }
            .totals { margin-top: 10px; }
            .total-row { display: flex; justify-content: space-between; margin-bottom: 4px; }
            .grand-total { font-size: 16px; font-weight: bold; margin-top: 8px; }
            .payment { margin-top: 15px; }
            .footer { margin-top: 20px; font-size: 10px; }
            @media print {
              body { width: auto; }
            }
          </style>
        </head>
        <body>
          <div class="header center">
            <h1>${data.restaurantName}</h1>
            ${data.restaurantAddress ? `<p>${data.restaurantAddress}</p>` : ''}
            ${data.restaurantPhone ? `<p>${data.restaurantPhone}</p>` : ''}
          </div>

          <div class="line"></div>

          <div class="meta">
            <div class="meta-row">
              <span>Check #${data.checkNumber}</span>
              <span>${this.formatDate(data.printedAt)}</span>
            </div>
            ${data.tableNumber ? `<div class="meta-row"><span>Table ${data.tableNumber}</span></div>` : ''}
            ${data.guestName ? `<div class="meta-row"><span>Guest: ${data.guestName}</span></div>` : ''}
            ${data.serverName ? `<div class="meta-row"><span>Server: ${data.serverName}</span></div>` : ''}
          </div>

          <div class="line"></div>

          <div class="items">
            ${data.items.map(item => `
              <div class="item">
                <div class="item-row">
                  <span class="item-name">${item.quantity}x ${item.name}</span>
                  <span>${this.formatCurrency(item.priceCents * item.quantity)}</span>
                </div>
                ${item.modifiers?.map(mod => `
                  <div class="modifier">+ ${mod.name} ${mod.priceCents ? this.formatCurrency(mod.priceCents) : ''}</div>
                `).join('') || ''}
              </div>
            `).join('')}
          </div>

          <div class="line"></div>

          <div class="totals">
            <div class="total-row">
              <span>Subtotal</span>
              <span>${this.formatCurrency(data.subtotalCents)}</span>
            </div>
            ${data.discountCents ? `
              <div class="total-row" style="color: green;">
                <span>Discount${data.discountReason ? ` (${data.discountReason})` : ''}</span>
                <span>-${this.formatCurrency(data.discountCents)}</span>
              </div>
            ` : ''}
            <div class="total-row">
              <span>Tax</span>
              <span>${this.formatCurrency(data.taxCents)}</span>
            </div>
            ${data.tipCents ? `
              <div class="total-row">
                <span>Tip</span>
                <span>${this.formatCurrency(data.tipCents)}</span>
              </div>
            ` : ''}
            <div class="double-line"></div>
            <div class="total-row grand-total">
              <span>TOTAL</span>
              <span>${this.formatCurrency(data.totalCents)}</span>
            </div>
          </div>

          ${data.paymentMethod ? `
            <div class="payment">
              <div class="line"></div>
              <div class="total-row">
                <span>${data.paymentMethod.toUpperCase()}</span>
                <span>${this.formatCurrency(data.amountTenderedCents || data.totalCents)}</span>
              </div>
              ${data.changeCents ? `
                <div class="total-row">
                  <span>Change</span>
                  <span>${this.formatCurrency(data.changeCents)}</span>
                </div>
              ` : ''}
            </div>
          ` : ''}

          <div class="footer center">
            <div class="line"></div>
            <p>Thank you for dining with us!</p>
            <p>Please come again</p>
          </div>
        </body>
      </html>
    `;

    // Open popup and print
    const printWindow = window.open('', '_blank', `width=${width + 50},height=600`);
    if (!printWindow) {
      throw new Error('Could not open print window. Please check popup blocker settings.');
    }

    printWindow.document.write(html);
    printWindow.document.close();

    // Use setTimeout instead of onload - document.write content is synchronous
    // Small delay ensures DOM is rendered before printing
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      // Don't auto-close - let user close after reviewing print dialog
    }, 250);
  }

  // ESC/POS thermal printer commands
  private async printThermal(data: ReceiptData): Promise<void> {
    // Check for Web USB API support
    if (!navigator.usb) {
      console.warn('Web USB API not supported, falling back to browser print');
      return this.printBrowser(data);
    }

    try {
      // Request USB device access
      const device = await navigator.usb.requestDevice({
        filters: [
          // Common thermal printer vendor IDs
          { vendorId: 0x04b8 }, // Epson
          { vendorId: 0x0519 }, // Star Micronics
          { vendorId: 0x0dd4 }, // Bixolon
        ],
      });

      await device.open();
      await device.selectConfiguration(1);
      await device.claimInterface(0);

      // Build ESC/POS commands
      const commands = this.buildEscPosCommands(data);

      // Find the output endpoint
      const endpoint = device.configuration?.interfaces[0]?.alternate?.endpoints.find(
        (e) => e.direction === 'out'
      );

      if (endpoint) {
        await device.transferOut(endpoint.endpointNumber, commands);
      }

      await device.close();
    } catch (error) {
      console.error('Thermal printer error:', error);
      // Fallback to browser print
      return this.printBrowser(data);
    }
  }

  // Network printer (e.g., Star CloudPRNT, Epson ePOS)
  private async printNetwork(data: ReceiptData): Promise<void> {
    if (!this.config.networkUrl) {
      throw new Error('Network printer URL not configured');
    }

    const commands = this.buildEscPosCommands(data);
    const base64 = btoa(String.fromCharCode(...commands));

    const response = await fetch(this.config.networkUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        type: 'raw',
        data: base64,
      }),
    });

    if (!response.ok) {
      throw new Error(`Network printer error: ${response.status}`);
    }
  }

  // Build ESC/POS command buffer
  private buildEscPosCommands(data: ReceiptData): Uint8Array {
    const encoder = new EscPosEncoder();

    // Initialize printer
    encoder.init();

    // Header
    encoder.align('center');
    encoder.bold(true);
    encoder.size(2);
    encoder.text(data.restaurantName);
    encoder.newline();
    encoder.size(1);
    encoder.bold(false);
    if (data.restaurantAddress) {
      encoder.text(data.restaurantAddress);
      encoder.newline();
    }
    if (data.restaurantPhone) {
      encoder.text(data.restaurantPhone);
      encoder.newline();
    }

    encoder.line();
    encoder.align('left');

    // Meta info
    encoder.text(`Check #${data.checkNumber}`);
    encoder.text(`  ${this.formatDate(data.printedAt)}`);
    encoder.newline();
    if (data.tableNumber) {
      encoder.text(`Table ${data.tableNumber}`);
      encoder.newline();
    }
    if (data.guestName) {
      encoder.text(`Guest: ${data.guestName}`);
      encoder.newline();
    }
    if (data.serverName) {
      encoder.text(`Server: ${data.serverName}`);
      encoder.newline();
    }

    encoder.line();

    // Items
    for (const item of data.items) {
      encoder.columns([
        { text: `${item.quantity}x ${item.name}`, width: 28 },
        { text: this.formatCurrency(item.priceCents * item.quantity), width: 10, align: 'right' },
      ]);
      if (item.modifiers) {
        for (const mod of item.modifiers) {
          encoder.text(`  + ${mod.name}`);
          encoder.newline();
        }
      }
    }

    encoder.line();

    // Totals
    encoder.columns([
      { text: 'Subtotal', width: 28 },
      { text: this.formatCurrency(data.subtotalCents), width: 10, align: 'right' },
    ]);

    if (data.discountCents) {
      encoder.columns([
        { text: `Discount${data.discountReason ? ` (${data.discountReason})` : ''}`, width: 28 },
        { text: `-${this.formatCurrency(data.discountCents)}`, width: 10, align: 'right' },
      ]);
    }

    encoder.columns([
      { text: 'Tax', width: 28 },
      { text: this.formatCurrency(data.taxCents), width: 10, align: 'right' },
    ]);

    if (data.tipCents) {
      encoder.columns([
        { text: 'Tip', width: 28 },
        { text: this.formatCurrency(data.tipCents), width: 10, align: 'right' },
      ]);
    }

    encoder.line();

    // Grand total
    encoder.bold(true);
    encoder.size(2);
    encoder.columns([
      { text: 'TOTAL', width: 14 },
      { text: this.formatCurrency(data.totalCents), width: 10, align: 'right' },
    ]);
    encoder.size(1);
    encoder.bold(false);

    // Payment info
    if (data.paymentMethod) {
      encoder.line();
      encoder.columns([
        { text: data.paymentMethod.toUpperCase(), width: 28 },
        { text: this.formatCurrency(data.amountTenderedCents || data.totalCents), width: 10, align: 'right' },
      ]);
      if (data.changeCents) {
        encoder.columns([
          { text: 'Change', width: 28 },
          { text: this.formatCurrency(data.changeCents), width: 10, align: 'right' },
        ]);
      }
    }

    // Footer
    encoder.newline();
    encoder.line();
    encoder.align('center');
    encoder.text('Thank you for dining with us!');
    encoder.newline();
    encoder.text('Please come again');
    encoder.newline();
    encoder.newline();
    encoder.newline();

    // Cut paper
    encoder.cut();

    return encoder.getBuffer();
  }

  // Print kitchen ticket (simpler format)
  public async printKitchenTicket(data: {
    orderNumber: string;
    tableNumber: string;
    items: Array<{
      name: string;
      quantity: number;
      modifiers?: Array<{ name: string }>;
      specialInstructions?: string;
      courseNumber?: number;
    }>;
    serverName?: string;
    createdAt: Date;
  }): Promise<void> {
    // Kitchen printer over TCP (iOS): big, loud, cut per ticket.
    const kitchenIp = this.config.kitchenIp || this.config.receiptIp;
    if (this.config.type === 'tcp' && kitchenIp) {
      const encoder = new EscPosEncoder();
      encoder.init();
      encoder.align('center');
      encoder.size(2);
      encoder.bold(true);
      encoder.text(`TABLE ${data.tableNumber}`);
      encoder.newline();
      encoder.size(1);
      encoder.bold(false);
      encoder.text(
        `${data.createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}${data.serverName ? ` · ${data.serverName}` : ''}`
      );
      encoder.newline();
      encoder.align('left');
      encoder.line();
      for (const item of data.items) {
        encoder.size(2);
        encoder.bold(true);
        encoder.text(`${item.quantity}x ${item.name}`);
        encoder.newline();
        encoder.size(1);
        encoder.bold(false);
        for (const mod of item.modifiers || []) {
          encoder.text(`   + ${mod.name}`);
          encoder.newline();
        }
        if (item.specialInstructions) {
          encoder.bold(true);
          encoder.text(`   ** ${item.specialInstructions}`);
          encoder.bold(false);
          encoder.newline();
        }
        if (item.courseNumber && item.courseNumber > 1) {
          encoder.text(`   course ${item.courseNumber}`);
          encoder.newline();
        }
      }
      encoder.line();
      encoder.newline();
      encoder.cut();
      return this.sendTcp(encoder.getBuffer(), kitchenIp);
    }

    const width = this.config.paperWidth === '58mm' ? 220 : 300;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kitchen Ticket</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Courier New', monospace;
              font-size: 14px;
              width: ${width}px;
              padding: 10px;
              background: white;
            }
            .center { text-align: center; }
            .bold { font-weight: bold; }
            .line { border-top: 2px dashed #000; margin: 10px 0; }
            .header { margin-bottom: 10px; }
            .header h1 { font-size: 24px; }
            .meta { margin-bottom: 10px; font-size: 12px; }
            .items { }
            .item { margin-bottom: 15px; }
            .item-main { font-size: 18px; font-weight: bold; }
            .item-qty { font-size: 22px; font-weight: bold; }
            .modifier { padding-left: 20px; font-size: 14px; }
            .special {
              padding: 5px;
              margin-top: 5px;
              background: #ffeb3b;
              font-weight: bold;
            }
            .course {
              font-size: 16px;
              font-weight: bold;
              background: #e0e0e0;
              padding: 5px;
              margin: 10px 0;
            }
          </style>
        </head>
        <body>
          <div class="header center">
            <h1>** ORDER **</h1>
            <p class="bold">#${data.orderNumber}</p>
          </div>

          <div class="line"></div>

          <div class="meta">
            <p><strong>Table:</strong> ${data.tableNumber}</p>
            ${data.serverName ? `<p><strong>Server:</strong> ${data.serverName}</p>` : ''}
            <p><strong>Time:</strong> ${data.createdAt.toLocaleTimeString()}</p>
          </div>

          <div class="line"></div>

          <div class="items">
            ${data.items.map(item => `
              <div class="item">
                <div class="item-main">
                  <span class="item-qty">${item.quantity}x</span> ${item.name}
                </div>
                ${item.modifiers?.map(mod => `
                  <div class="modifier">>>> ${mod.name}</div>
                `).join('') || ''}
                ${item.specialInstructions ? `
                  <div class="special">!! ${item.specialInstructions} !!</div>
                ` : ''}
              </div>
            `).join('')}
          </div>

          <div class="line"></div>
          <div class="center bold">
            <p>--- END ORDER ---</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', `width=${width + 50},height=600`);
    if (!printWindow) {
      throw new Error('Could not open print window');
    }

    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => printWindow.close(), 1000);
    };
  }
}

// Simple ESC/POS command encoder
class EscPosEncoder {
  private buffer: number[] = [];

  // ESC/POS Commands
  private static readonly ESC = 0x1b;
  private static readonly GS = 0x1d;
  private static readonly LF = 0x0a;

  init() {
    this.buffer.push(EscPosEncoder.ESC, 0x40); // Initialize printer
  }

  text(str: string) {
    for (let i = 0; i < str.length; i++) {
      this.buffer.push(str.charCodeAt(i));
    }
  }

  newline() {
    this.buffer.push(EscPosEncoder.LF);
  }

  bold(on: boolean) {
    this.buffer.push(EscPosEncoder.ESC, 0x45, on ? 1 : 0);
  }

  size(n: number) {
    // n = 1 for normal, 2 for double height/width
    const s = n === 2 ? 0x11 : 0x00;
    this.buffer.push(EscPosEncoder.GS, 0x21, s);
  }

  align(alignment: 'left' | 'center' | 'right') {
    const a = alignment === 'center' ? 1 : alignment === 'right' ? 2 : 0;
    this.buffer.push(EscPosEncoder.ESC, 0x61, a);
  }

  line() {
    this.text('--------------------------------');
    this.newline();
  }

  columns(cols: Array<{ text: string; width: number; align?: 'left' | 'right' }>) {
    let line = '';
    for (const col of cols) {
      let text = col.text.substring(0, col.width);
      if (col.align === 'right') {
        text = text.padStart(col.width);
      } else {
        text = text.padEnd(col.width);
      }
      line += text;
    }
    this.text(line);
    this.newline();
  }

  cut() {
    this.buffer.push(EscPosEncoder.GS, 0x56, 0x00); // Full cut
  }

  getBuffer(): Uint8Array {
    return new Uint8Array(this.buffer);
  }
}

// Export singleton instance
export const printerService = new PrinterService();
export type { ReceiptData, PrinterConfig };
